import type { InsightDominio } from '../api/bridge'
import type { InsightFallback } from './AiInsightPanel'
import { BI_NAV_ALL, INSIGHTS_NAV_ALL } from './DomainNav'

/**
 * Mapa rota → domínio do motor de insights (`/insights/resumo`).
 * Usado pelo chat Fiesta IA (contexto) e pela gaveta «Insights» do cabeçalho
 * quando a tela não registra leitura própria. Ordem importa: rotas mais
 * específicas antes; `/` por último.
 */
export const PAGE_DOMAINS: Array<{
  path: string
  dominio: InsightDominio
  label: string
}> = [
  { path: '/insights/comercial', dominio: 'comercial', label: 'comercial' },
  { path: '/insights/produtos', dominio: 'mix', label: 'mix e SKUs' },
  { path: '/insights/logistica', dominio: 'logistica', label: 'logística' },
  { path: '/insights/financeiro', dominio: 'financeiro', label: 'financeiro' },
  { path: '/insights/alertas', dominio: 'operacoes', label: 'operações' },
  { path: '/insights/prioridades', dominio: 'home', label: 'prioridades' },
  { path: '/insights', dominio: 'home', label: 'negócio' },
  { path: '/pedidos', dominio: 'comercial', label: 'pedidos' },
  { path: '/vendas', dominio: 'comercial', label: 'vendas' },
  { path: '/produtos', dominio: 'mix', label: 'mix e SKUs' },
  { path: '/margem-anuncios', dominio: 'financeiro', label: 'margem por anúncio' },
  { path: '/estoque-full', dominio: 'mix', label: 'estoque Full' },
  { path: '/recompra', dominio: 'mix', label: 'recompra' },
  { path: '/promocoes', dominio: 'comercial', label: 'promoções' },
  { path: '/ads', dominio: 'comercial', label: 'ads' },
  { path: '/score-anuncios', dominio: 'comercial', label: 'score dos anúncios' },
  { path: '/reclamacoes', dominio: 'operacoes', label: 'reclamações' },
  { path: '/fretes', dominio: 'logistica', label: 'logística' },
  { path: '/pagamentos', dominio: 'financeiro', label: 'financeiro' },
  { path: '/despesas', dominio: 'despesas', label: 'despesas' },
  { path: '/margens', dominio: 'financeiro', label: 'margens' },
  { path: '/fiscal', dominio: 'fiscal', label: 'fiscal' },
  { path: '/operacoes', dominio: 'operacoes', label: 'operações' },
  { path: '/clientes', dominio: 'clientes', label: 'clientes' },
  { path: '/agenda', dominio: 'agenda', label: 'agenda' },
  { path: '/', dominio: 'home', label: 'negócio' },
]

export function pageDomain(pathname: string) {
  return PAGE_DOMAINS.find(
    ({ path }) => pathname === path || (path !== '/' && pathname.startsWith(`${path}/`)),
  )
}

/**
 * Painéis sem insights da IA (decisão da Mariana, 23/09/2026): Buy Box, Consulta
 * Marcas INPI e Usuários. Perfil, Configurações e Login são telas de sistema.
 */
const ROTAS_SEM_INSIGHTS = ['/buybox', '/marcas', '/usuarios', '/perfil', '/configuracoes', '/login']

export function rotaSemInsights(pathname: string) {
  return ROTAS_SEM_INSIGHTS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/** Nome do painel como está no menu lateral. */
export function tituloDoPainel(pathname: string): string {
  const todos = [...INSIGHTS_NAV_ALL, ...BI_NAV_ALL]
  const exato = todos.find((i) => i.to === pathname)
  if (exato) return exato.label
  const prefixo = todos
    .filter((i) => i.to !== '/' && pathname.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0]
  if (prefixo) return prefixo.label
  if (pathname === '/vendas') return 'Vendas Diárias'
  return 'este painel'
}

/** Texto de partida enquanto o motor não responde — sem número inventado. */
export function fallbackPorDominio(dominio: InsightDominio, painel: string): InsightFallback {
  const base = {
    title: `Leitura de ${painel}`,
    detail: 'Leitura do período aberto — a mesma que a Fiesta IA usa no chat.',
  }
  switch (dominio) {
    case 'comercial':
      return {
        ...base,
        recommendations: [
          { title: 'Ver margens', detail: 'CMV e lucro por pedido.', to: '/margens' },
          { title: 'Ver fiscal', detail: 'Cobertura NF e sem NF.', to: '/fiscal' },
        ],
      }
    case 'mix':
      return {
        ...base,
        recommendations: [
          { title: 'Vendas por Anúncio', detail: 'Receita e unidades por anúncio.', to: '/produtos' },
          { title: 'Recompra', detail: 'O que comprar pelo giro real.', to: '/recompra' },
        ],
      }
    case 'financeiro':
      return {
        ...base,
        recommendations: [
          { title: 'Margem por Anúncio', detail: 'MC oficial e ajustada.', to: '/margem-anuncios' },
          { title: 'Ver fiscal', detail: 'Pedidos sem NF bloqueiam margem.', to: '/fiscal' },
        ],
      }
    case 'logistica':
      return {
        ...base,
        recommendations: [
          { title: 'Ver pedidos', detail: 'Status e prazos do período.', to: '/pedidos' },
          { title: 'Estoque Full', detail: 'Reposição e encalhe no Full.', to: '/estoque-full' },
        ],
      }
    case 'operacoes':
      return {
        ...base,
        recommendations: [
          { title: 'Reclamações', detail: 'Fila e prazos das 14h.', to: '/reclamacoes' },
          { title: 'Ver alertas', detail: 'Desvios por impacto.', to: '/insights/alertas' },
        ],
      }
    default:
      return {
        ...base,
        recommendations: [
          { title: 'Ver margens', detail: 'Margem por pedido e por anúncio.', to: '/margens' },
          { title: 'Ver pedidos', detail: 'Funil e lista do período.', to: '/pedidos' },
        ],
      }
  }
}
